import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
} from 'react'
import { Dropdown, Modal, Spin } from 'antd'
import type { MenuProps } from 'antd'
import {
  AppstoreOutlined,
  ArrowDownOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  ArrowUpOutlined,
  CloseOutlined,
  ColumnWidthOutlined,
  CopyOutlined,
  DeleteOutlined,
  DiffOutlined,
  EditOutlined,
  FileAddOutlined,
  FileOutlined,
  FolderAddOutlined,
  FolderOutlined,
  HistoryOutlined,
  HomeOutlined,
  ReloadOutlined,
  ScissorOutlined,
  SearchOutlined,
  SnippetsOutlined,
  StarOutlined,
  UnorderedListOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { toast } from '@crosscode/gravity-components'
import {
  ftpAddFavorite,
  ftpAddRecent,
  ftpCompare,
  ftpConnect,
  ftpCopy,
  ftpDelete,
  ftpDisconnect,
  ftpDownloadBlob,
  ftpDownloadUrl,
  ftpGetFavorites,
  ftpGetRecent,
  ftpGetVersions,
  ftpList,
  ftpMkdir,
  ftpMkfile,
  ftpRead,
  ftpRemoveFavorite,
  ftpRename,
  ftpRestoreVersion,
  ftpSave,
} from './api'
import { FtpDiffModal } from './FtpDiffModal'
import { FtpLocalPanel } from './FtpLocalPanel'
import { FtpTransferPanel } from './FtpTransferPanel'
import { FtpVersionsModal } from './FtpVersionsModal'
import { addLocalFiles, buildLocalIndex, getLocalFile } from './localBrowser'
import { useTransferQueue } from './useTransferQueue'
import styles from './FtpClient.module.css'
import type {
  FtpClipboard,
  FtpCompareState,
  FtpConnectionState,
  FtpEditorState,
  FtpFavorite,
  FtpFileItem,
  FtpLayoutMode,
  FtpLogEntry,
  FtpOpenOptions,
  FtpRecentItem,
  FtpSidebarTab,
  FtpSortConfig,
  FtpSortField,
  FtpVersionsState,
  FtpViewerState,
  FtpViewMode,
  LocalFileEntry,
} from './types'
import {
  breadcrumbParts,
  CONNECTION_LABELS,
  filterAndSortItems,
  formatDate,
  formatFileSize,
  formatPermissionsLabel,
  getOpenAction,
  isDirectory,
  joinPath,
  normalizePath,
  parentPath,
  totalSize,
} from './utils'

interface FtpClientModalProps {
  open: boolean
  options: FtpOpenOptions | null
  onClose: () => void
}

function logId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
}

function nowTime() {
  return new Date().toLocaleTimeString('ru-RU')
}

export function FtpClientModal({ open, options, onClose }: FtpClientModalProps) {
  const [connectionId, setConnectionId] = useState<string | null>(null)
  const [protocol, setProtocol] = useState('SFTP')
  const [connState, setConnState] = useState<FtpConnectionState>('idle')
  const [currentPath, setCurrentPath] = useState('/')
  const [pathInput, setPathInput] = useState('/')
  const [items, setItems] = useState<FtpFileItem[]>([])
  const [listLoading, setListLoading] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [clipboard, setClipboard] = useState<FtpClipboard | null>(null)
  const [historyBack, setHistoryBack] = useState<string[]>([])
  const [historyForward, setHistoryForward] = useState<string[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [sort, setSort] = useState<FtpSortConfig>({ field: 'name', direction: 'asc' })
  const [viewMode, setViewMode] = useState<FtpViewMode>('table')
  const [sidebarTab, setSidebarTab] = useState<FtpSidebarTab>('folders')
  const [favorites, setFavorites] = useState<FtpFavorite[]>([])
  const [recent, setRecent] = useState<FtpRecentItem[]>([])
  const [statusText, setStatusText] = useState('Готов')
  const [logs, setLogs] = useState<FtpLogEntry[]>([])
  const [logOpen, setLogOpen] = useState(false)
  const [editor, setEditor] = useState<FtpEditorState | null>(null)
  const [viewer, setViewer] = useState<FtpViewerState | null>(null)
  const [contextItem, setContextItem] = useState<FtpFileItem | null>(null)
  const [contextPos, setContextPos] = useState<{ x: number; y: number } | null>(null)
  const [layoutMode, setLayoutMode] = useState<FtpLayoutMode>('dual')
  const [localEntries, setLocalEntries] = useState<LocalFileEntry[]>([])
  const [localRootLabel, setLocalRootLabel] = useState('Локальный компьютер')
  const [localPath, setLocalPath] = useState('/')
  const [localPathInput, setLocalPathInput] = useState('/')
  const [localSelected, setLocalSelected] = useState<Set<string>>(new Set())
  const [versionsState, setVersionsState] = useState<FtpVersionsState | null>(null)
  const [versionsLoading, setVersionsLoading] = useState(false)
  const [compareState, setCompareState] = useState<FtpCompareState | null>(null)
  const [compareLoading, setCompareLoading] = useState(false)
  const [versionPreview, setVersionPreview] = useState<string | null>(null)

  const uploadRef = useRef<HTMLInputElement>(null)
  const localDirRef = useRef<HTMLInputElement>(null)
  const localFileRef = useRef<HTMLInputElement>(null)
  const editorRef = useRef<HTMLTextAreaElement>(null)
  const connectionIdRef = useRef<string | null>(null)
  const shellRef = useRef<HTMLDivElement>(null)
  const localEntriesRef = useRef<LocalFileEntry[]>([])

  const { tasks: transferTasks, panelOpen: transferOpen, setPanelOpen: setTransferOpen, enqueue, clearDone, activeCount } =
    useTransferQueue()

  connectionIdRef.current = connectionId
  localEntriesRef.current = localEntries

  const addLog = useCallback((message: string, type: FtpLogEntry['type'] = 'info') => {
    setLogs((prev) => [...prev.slice(-199), { id: logId(), time: nowTime(), message, type }])
  }, [])

  const resetState = useCallback(() => {
    setConnectionId(null)
    setProtocol('SFTP')
    setConnState('idle')
    setCurrentPath('/')
    setPathInput('/')
    setItems([])
    setSelected(new Set())
    setClipboard(null)
    setHistoryBack([])
    setHistoryForward([])
    setSearchQuery('')
    setSort({ field: 'name', direction: 'asc' })
    setViewMode('table')
    setSidebarTab('folders')
    setFavorites([])
    setRecent([])
    setStatusText('Готов')
    setLogs([])
    setLogOpen(false)
    setEditor(null)
    setViewer(null)
    setContextItem(null)
    setContextPos(null)
    setLocalEntries([])
    setLocalRootLabel('Локальный компьютер')
    setLocalPath('/')
    setLocalPathInput('/')
    setLocalSelected(new Set())
    setVersionsState(null)
    setCompareState(null)
    setVersionPreview(null)
  }, [])

  const loadFavorites = useCallback(async () => {
    try {
      const data = await ftpGetFavorites()
      if (data.success) setFavorites(data.favorites || [])
    } catch {
      /* ignore */
    }
  }, [])

  const loadRecent = useCallback(async () => {
    try {
      const data = await ftpGetRecent()
      if (data.success) setRecent(data.recent || [])
    } catch {
      /* ignore */
    }
  }, [])

  const loadDirectory = useCallback(
    async (cid: string, path: string, recordRecent = true) => {
      const normalized = normalizePath(path)
      setListLoading(true)
      setStatusText('Загрузка...')
      setSelected(new Set())
      try {
        const data = await ftpList(cid, normalized)
        if (!data.success) throw new Error(data.error || 'Ошибка загрузки')
        setItems(data.items || [])
        setCurrentPath(normalized)
        setPathInput(normalized)
        addLog(`Загружена директория: ${normalized} (${(data.items || []).length} элементов)`)
        setStatusText('Готов')
        if (recordRecent) {
          const name = normalized === '/' ? '/' : normalized.split('/').pop() || normalized
          void ftpAddRecent(normalized, name, 'directory').then(() => loadRecent())
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Ошибка загрузки'
        addLog(msg, 'error')
        setStatusText(`Ошибка: ${msg}`)
        toast.error(msg)
      } finally {
        setListLoading(false)
      }
    },
    [addLog, loadRecent],
  )

  const navigateTo = useCallback(
    (path: string, recordHistory = true) => {
      if (!connectionId) return
      const normalized = normalizePath(path)
      if (recordHistory && normalized !== currentPath) {
        setHistoryBack((prev) => [...prev, currentPath])
        setHistoryForward([])
      }
      void loadDirectory(connectionId, normalized)
    },
    [connectionId, currentPath, loadDirectory],
  )

  const connect = useCallback(async () => {
    if (!options?.host) {
      toast.error('Не указан хост')
      return
    }
    setConnState('connecting')
    setStatusText('Подключение...')
    addLog(`Подключение к ${options.connectionType.toUpperCase()} ${options.host}...`)
    const start = Date.now()
    try {
      const data = await ftpConnect(options.connectionType, options.host)
      if (!data.success || !data.connectionId) {
        throw new Error(data.error || 'Ошибка подключения')
      }
      setConnectionId(data.connectionId)
      setProtocol((data.protocol || 'sftp').toUpperCase())
      setConnState('connected')
      const sec = ((Date.now() - start) / 1000).toFixed(1)
      addLog(`Подключено за ${sec}с`, 'success')
      setStatusText('Подключено')
      await Promise.all([
        loadDirectory(data.connectionId, options.initialPath || '/'),
        loadFavorites(),
        loadRecent(),
      ])
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Ошибка подключения'
      setConnState('error')
      setStatusText(`Ошибка: ${msg}`)
      addLog(msg, 'error')
      toast.error(msg)
    }
  }, [addLog, loadDirectory, loadFavorites, loadRecent, options])

  const disconnect = useCallback(async () => {
    const cid = connectionIdRef.current
    if (cid) {
      try {
        await ftpDisconnect(cid)
        addLog('Отключено', 'info')
      } catch {
        /* ignore */
      }
    }
    resetState()
  }, [addLog, resetState])

  useEffect(() => {
    if (!open || !options) return
    resetState()
    void connect()
    return () => {
      const cid = connectionIdRef.current
      if (cid) void ftpDisconnect(cid).catch(() => {})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, options?.host, options?.connectionType])

  useEffect(() => {
    return () => {
      if (viewer?.url.startsWith('blob:')) URL.revokeObjectURL(viewer.url)
    }
  }, [viewer])

  useEffect(() => {
    return () => {
      if (viewer?.url.startsWith('blob:')) URL.revokeObjectURL(viewer.url)
    }
  }, [viewer])

  const filteredItems = useMemo(
    () => filterAndSortItems(items, searchQuery, sort),
    [items, searchQuery, sort],
  )

  const folderTree = useMemo(
    () => items.filter(isDirectory).sort((a, b) => a.name.localeCompare(b.name, 'ru')),
    [items],
  )

  const hasSelection = selected.size > 0
  const singleSelection = selected.size === 1
  const canPaste = !!clipboard && clipboard.files.length > 0

  const toggleSort = (field: FtpSortField) => {
    setSort((prev) =>
      prev.field === field
        ? { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { field, direction: 'asc' },
    )
  }

  const toggleSelect = (item: FtpFileItem, e?: ReactMouseEvent) => {
    if (e?.ctrlKey || e?.metaKey) {
      setSelected((prev) => {
        const next = new Set(prev)
        if (next.has(item.path)) next.delete(item.path)
        else next.add(item.path)
        return next
      })
      return
    }
    if (e?.shiftKey && selected.size > 0) {
      const paths = filteredItems.map((i) => i.path)
      const last = Array.from(selected).pop()!
      const a = paths.indexOf(last)
      const b = paths.indexOf(item.path)
      if (a >= 0 && b >= 0) {
        const [from, to] = a < b ? [a, b] : [b, a]
        setSelected(new Set(paths.slice(from, to + 1)))
      }
      return
    }
    setSelected(new Set([item.path]))
  }

  const selectAll = () => setSelected(new Set(filteredItems.map((i) => i.path)))

  const handleCopy = () => {
    const files = Array.from(selected)
      .map((p) => items.find((i) => i.path === p))
      .filter(Boolean) as FtpFileItem[]
    if (!files.length) return
    setClipboard({ mode: 'copy', files })
    addLog(`Скопировано: ${files.length}`, 'info')
  }

  const handleCut = () => {
    const files = Array.from(selected)
      .map((p) => items.find((i) => i.path === p))
      .filter(Boolean) as FtpFileItem[]
    if (!files.length) return
    setClipboard({ mode: 'move', files })
    addLog(`Вырезано: ${files.length}`, 'info')
  }

  const handlePaste = async () => {
    if (!connectionId || !clipboard?.files.length) return
    setStatusText('Вставка...')
    let ok = 0
    for (const file of clipboard.files) {
      if (isDirectory(file)) {
        addLog(`Пропущено (папки): ${file.name}`, 'error')
        continue
      }
      const fileName = file.path.split('/').pop()!
      const destPath = joinPath(currentPath, fileName)
      try {
        const res = await ftpCopy(connectionId, file.path, destPath, clipboard.mode === 'move')
        if (!res.success) throw new Error(res.error)
        ok++
      } catch (err) {
        addLog(
          `${file.name}: ${err instanceof Error ? err.message : 'ошибка'}`,
          'error',
        )
      }
    }
    if (clipboard.mode === 'move') setClipboard(null)
    await loadDirectory(connectionId, currentPath, false)
    addLog(`Вставлено: ${ok}`, 'success')
    setStatusText('Готов')
  }

  const handleDelete = async () => {
    if (!connectionId || !selected.size) return
    const files = Array.from(selected)
      .map((p) => items.find((i) => i.path === p))
      .filter(Boolean) as FtpFileItem[]
    Modal.confirm({
      title: `Удалить ${files.length} элемент(ов)?`,
      okText: 'Удалить',
      okType: 'danger',
      cancelText: 'Отмена',
      onOk: async () => {
        setStatusText('Удаление...')
        for (const file of files) {
          try {
            const res = await ftpDelete(
              connectionId,
              file.path,
              isDirectory(file) ? 'directory' : 'file',
            )
            if (!res.success) throw new Error(res.error)
          } catch (err) {
            addLog(
              `${file.name}: ${err instanceof Error ? err.message : 'ошибка'}`,
              'error',
            )
          }
        }
        setSelected(new Set())
        await loadDirectory(connectionId, currentPath, false)
        addLog(`Удалено: ${files.length}`, 'success')
        setStatusText('Готов')
      },
    })
  }

  const handleRename = async () => {
    if (!connectionId || selected.size !== 1) return
    const path = Array.from(selected)[0]
    const item = items.find((i) => i.path === path)
    if (!item) return
    const newName = window.prompt('Новое имя:', item.name)
    if (!newName || newName === item.name) return
    setStatusText('Переименование...')
    try {
      const res = await ftpRename(connectionId, item.path, newName)
      if (!res.success) throw new Error(res.error)
      await loadDirectory(connectionId, currentPath, false)
      addLog(`Переименовано: ${item.name} → ${newName}`, 'success')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Ошибка')
    } finally {
      setStatusText('Готов')
    }
  }

  const handleMkdir = async () => {
    if (!connectionId) return
    const name = window.prompt('Имя папки:')
    if (!name) return
    try {
      const res = await ftpMkdir(connectionId, currentPath, name)
      if (!res.success) throw new Error(res.error)
      await loadDirectory(connectionId, currentPath, false)
      addLog(`Создана папка: ${name}`, 'success')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Ошибка')
    }
  }

  const handleMkfile = async () => {
    if (!connectionId) return
    const name = window.prompt('Имя файла:')
    if (!name) return
    try {
      const res = await ftpMkfile(connectionId, currentPath, name)
      if (!res.success) throw new Error(res.error)
      await loadDirectory(connectionId, currentPath, false)
      addLog(`Создан файл: ${name}`, 'success')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Ошибка')
    }
  }

  const enqueueUploadFiles = useCallback(
    (files: File[], remoteDir: string) => {
      if (!connectionId || !files.length) return
      enqueue(
        files.map((file) => ({
          direction: 'upload' as const,
          name: file.name,
          remotePath: joinPath(remoteDir, file.name),
          remoteDir,
          connectionId,
          localFile: file,
        })),
      )
      addLog(`В очередь загрузки: ${files.length} файл(ов)`, 'info')
      setStatusText(`В очереди: ${files.length} загрузок`)
    },
    [addLog, connectionId, enqueue],
  )

  const handleUpload = (fileList: FileList | null) => {
    if (!fileList?.length) return
    enqueueUploadFiles(Array.from(fileList), currentPath)
    if (connectionId) {
      setTimeout(() => void loadDirectory(connectionId, currentPath, false), 500)
    }
  }

  const addDownloadedToLocal = useCallback((blob: Blob, name: string) => {
    const file = new File([blob], name, { type: blob.type || 'application/octet-stream' })
    setLocalEntries((prev) => addLocalFiles(prev, localPath, [file]))
    addLog(`Скачан в локальную панель: ${name}`, 'success')
  }, [addLog, localPath])

  const enqueueDownloadFiles = useCallback(
    (remoteFiles: FtpFileItem[]) => {
      if (!connectionId) return
      const files = remoteFiles.filter((f) => !isDirectory(f))
      if (!files.length) return
      enqueue(
        files.map((file) => ({
          direction: 'download' as const,
          name: file.name,
          remotePath: file.path,
          remoteDir: currentPath,
          connectionId,
          onDownloaded: addDownloadedToLocal,
        })),
      )
      addLog(`В очередь скачивания: ${files.length} файл(ов)`, 'info')
    },
    [addDownloadedToLocal, addLog, connectionId, currentPath, enqueue],
  )

  const uploadLocalSelection = () => {
    const files = Array.from(localSelected)
      .map((p) => getLocalFile(localEntries, p))
      .filter((f): f is LocalFileEntry => !!f?.file)
      .map((f) => f.file!)
    if (!files.length) {
      toast.error('Выберите файлы на локальной панели')
      return
    }
    enqueueUploadFiles(files, currentPath)
    if (connectionId) {
      setTimeout(() => void loadDirectory(connectionId, currentPath, false), 800)
    }
  }

  const downloadRemoteSelection = () => {
    const remoteFiles = Array.from(selected)
      .map((p) => items.find((i) => i.path === p))
      .filter((f): f is FtpFileItem => !!f)
    if (!remoteFiles.length) {
      toast.error('Выберите файлы на сервере')
      return
    }
    enqueueDownloadFiles(remoteFiles)
  }

  const handleLocalFolderPick = (fileList: FileList | null) => {
    if (!fileList?.length) return
    const { rootLabel, entries } = buildLocalIndex(fileList)
    setLocalEntries(entries)
    setLocalRootLabel(rootLabel)
    setLocalPath('/')
    setLocalPathInput('/')
    setLocalSelected(new Set())
    addLog(`Локальная папка: ${entries.length} элементов`, 'info')
  }

  const handleLocalDrop = (files: FileList) => {
    setLocalEntries((prev) => addLocalFiles(prev, localPath, Array.from(files)))
    addLog(`Добавлено локально: ${files.length} файл(ов)`, 'info')
  }

  const toggleLocalSelect = (item: LocalFileEntry, e?: ReactMouseEvent) => {
    if (e?.ctrlKey || e?.metaKey) {
      setLocalSelected((prev) => {
        const next = new Set(prev)
        if (next.has(item.path)) next.delete(item.path)
        else next.add(item.path)
        return next
      })
      return
    }
    setLocalSelected(new Set([item.path]))
  }

  const openLocalItem = (item: LocalFileEntry) => {
    if (item.type === 'directory') {
      const p = normalizePath(item.path)
      setLocalPath(p)
      setLocalPathInput(p)
      setLocalSelected(new Set())
    }
  }

  const showVersions = async (item: FtpFileItem) => {
    if (!connectionId || isDirectory(item)) return
    setVersionsLoading(true)
    setVersionsState({ filePath: item.path, fileName: item.name, versions: [] })
    try {
      const data = await ftpGetVersions(connectionId, item.path)
      if (!data.success) throw new Error(data.error)
      setVersionsState({
        filePath: item.path,
        fileName: item.name,
        versions: data.versions || [],
      })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Ошибка')
      setVersionsState(null)
    } finally {
      setVersionsLoading(false)
    }
  }

  const restoreVersion = async (index: number) => {
    if (!connectionId || !versionsState) return
    try {
      const res = await ftpRestoreVersion(connectionId, versionsState.filePath, index)
      if (!res.success) throw new Error(res.error)
      toast.success('Версия восстановлена')
      setVersionsState(null)
      await loadDirectory(connectionId, currentPath, false)
      addLog(`Восстановлена версия: ${versionsState.fileName}`, 'success')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Ошибка')
    }
  }

  const compareSelectedFiles = async () => {
    if (!connectionId || selected.size !== 2) {
      toast.error('Выберите ровно 2 файла для сравнения')
      return
    }
    const files = Array.from(selected)
      .map((p) => items.find((i) => i.path === p))
      .filter((f): f is FtpFileItem => !!f && !isDirectory(f))
    if (files.length !== 2) {
      toast.error('Сравнение только для двух файлов')
      return
    }
    const [file1, file2] = files
    setCompareLoading(true)
    setCompareState({
      path1: file1.path,
      path2: file2.path,
      name1: file1.name,
      name2: file2.name,
      diff: [],
    })
    try {
      const data = await ftpCompare(connectionId, file1.path, file2.path)
      if (!data.success) throw new Error(data.error)
      setCompareState({
        path1: file1.path,
        path2: file2.path,
        name1: file1.name,
        name2: file2.name,
        diff: data.diff || [],
      })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Ошибка сравнения')
      setCompareState(null)
    } finally {
      setCompareLoading(false)
    }
  }

  const downloadFile = (filePath: string) => {
    if (!connectionId) return
    window.open(ftpDownloadUrl(connectionId, filePath), '_blank')
  }

  const openItem = async (item: FtpFileItem) => {
    if (!connectionId) return
    if (isDirectory(item)) {
      navigateTo(item.path)
      return
    }
    void ftpAddRecent(item.path, item.name, 'file').then(() => loadRecent())
    const action = getOpenAction(item.name)
    if (action === 'download') {
      downloadFile(item.path)
      return
    }
    if (action === 'text') {
      try {
        const data = await ftpRead(connectionId, item.path)
        if (!data.success) throw new Error(data.error)
        setEditor({
          path: item.path,
          name: item.name,
          content: data.content || '',
          originalContent: data.content || '',
        })
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Ошибка чтения')
      }
      return
    }
    try {
      if (action === 'image') {
        const blob = await ftpDownloadBlob(connectionId, item.path)
        const url = URL.createObjectURL(blob)
        setViewer({ path: item.path, name: item.name, kind: 'image', url })
      } else {
        setViewer({
          path: item.path,
          name: item.name,
          kind: 'pdf',
          url: ftpDownloadUrl(connectionId, item.path),
        })
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Ошибка открытия')
    }
  }

  const saveEditor = async () => {
    if (!connectionId || !editor) return
    try {
      const res = await ftpSave(connectionId, editor.path, editor.content)
      if (!res.success) throw new Error(res.error)
      toast.success('Файл сохранён')
      setEditor(null)
      await loadDirectory(connectionId, currentPath, false)
      addLog(`Сохранён: ${editor.name}`, 'success')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Ошибка сохранения')
    }
  }

  const addToFavorites = async (path: string) => {
    try {
      const res = await ftpAddFavorite(path)
      if (!res.success) throw new Error(res.error)
      await loadFavorites()
      addLog(`В избранное: ${path}`, 'success')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Ошибка')
    }
  }

  const handleClose = () => {
    void disconnect()
    onClose()
  }

  const contextMenuItems: MenuProps['items'] = contextItem
    ? [
        { key: 'open', label: 'Открыть', icon: <FolderOutlined /> },
        { key: 'download', label: 'Скачать', icon: <ArrowDownOutlined />, disabled: isDirectory(contextItem) },
        { type: 'divider' },
        { key: 'copy', label: 'Копировать', icon: <CopyOutlined /> },
        { key: 'cut', label: 'Вырезать', icon: <ScissorOutlined /> },
        { key: 'paste', label: 'Вставить', icon: <SnippetsOutlined />, disabled: !canPaste },
        { type: 'divider' },
        { key: 'rename', label: 'Переименовать', icon: <EditOutlined /> },
        { key: 'delete', label: 'Удалить', icon: <DeleteOutlined />, danger: true },
        { type: 'divider' },
        { key: 'favorite', label: 'В избранное', icon: <StarOutlined /> },
        { type: 'divider' },
        {
          key: 'versions',
          label: 'Версии',
          icon: <HistoryOutlined />,
          disabled: isDirectory(contextItem),
        },
        {
          key: 'downloadQueue',
          label: 'Скачать в локальную панель',
          icon: <ArrowDownOutlined />,
          disabled: isDirectory(contextItem),
        },
      ]
    : []

  const onContextAction = (key: string) => {
    if (!contextItem) return
    setSelected(new Set([contextItem.path]))
    switch (key) {
      case 'open':
        void openItem(contextItem)
        break
      case 'download':
        downloadFile(contextItem.path)
        break
      case 'copy':
        setClipboard({ mode: 'copy', files: [contextItem] })
        break
      case 'cut':
        setClipboard({ mode: 'move', files: [contextItem] })
        break
      case 'paste':
        void handlePaste()
        break
      case 'rename':
        void handleRename()
        break
      case 'delete':
        void handleDelete()
        break
      case 'favorite':
        void addToFavorites(contextItem.path)
        break
      case 'versions':
        void showVersions(contextItem)
        break
      case 'downloadQueue':
        enqueueDownloadFiles([contextItem])
        break
    }
    setContextPos(null)
  }

  useEffect(() => {
    if (!contextPos) return
    const close = () => setContextPos(null)
    window.addEventListener('click', close)
    return () => window.removeEventListener('click', close)
  }, [contextPos])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (editor) {
        if ((e.ctrlKey || e.metaKey) && e.key === 's') {
          e.preventDefault()
          void saveEditor()
        }
        return
      }
      const tag = (e.target as HTMLElement)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return
      if (!shellRef.current?.contains(e.target as Node)) return

      if (e.ctrlKey || e.metaKey) {
        if (e.key === 'c') { e.preventDefault(); handleCopy() }
        if (e.key === 'x') { e.preventDefault(); handleCut() }
        if (e.key === 'v') { e.preventDefault(); void handlePaste() }
        if (e.key === 'a') { e.preventDefault(); selectAll() }
      } else {
        if (e.key === 'Delete') { e.preventDefault(); void handleDelete() }
        if (e.key === 'F2') { e.preventDefault(); void handleRename() }
        if (e.key === 'F5') {
          e.preventDefault()
          if (connectionId) void loadDirectory(connectionId, currentPath, false)
        }
        if (e.key === 'Backspace' && e.altKey) {
          e.preventDefault()
          if (historyBack.length) {
            const prev = historyBack[historyBack.length - 1]
            setHistoryBack((h) => h.slice(0, -1))
            setHistoryForward((h) => [...h, currentPath])
            if (connectionId) void loadDirectory(connectionId, prev, false)
          }
        }
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  if (!open) return null

  const connLabel =
    connState === 'connected'
      ? 'Подключено'
      : connState === 'connecting'
        ? 'Подключение...'
        : connState === 'error'
          ? 'Ошибка'
          : 'Не подключено'

  const dotClass =
    connState === 'connected'
      ? styles.dotConnected
      : connState === 'connecting'
        ? styles.dotLoading
        : connState === 'error'
          ? styles.dotError
          : ''

  const title = options?.title || 'FTP/SFTP Клиент'
  const connInfo = options
    ? `${CONNECTION_LABELS[options.connectionType]} · ${protocol} · ${options.host}`
    : ''

  const crumbs = breadcrumbParts(currentPath)
  const itemCount = filteredItems.length
  const folderCount = filteredItems.filter(isDirectory).length
  const fileCount = itemCount - folderCount

  const sortMark = (field: FtpSortField) =>
    sort.field === field ? (sort.direction === 'asc' ? '▲' : '▼') : '⇅'

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-label="FTP клиент">
      <div className={styles.shell} ref={shellRef}>
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            <h2>{title}</h2>
            <div className={styles.headerMeta}>
              <span className={styles.connectionPill}>
                <span className={`${styles.dot} ${dotClass}`} />
                {connLabel}
              </span>
              {connInfo ? <span>{connInfo}</span> : null}
            </div>
          </div>
          <div className={styles.headerActions}>
            <button
              type="button"
              className={styles.layoutToggle}
              title="Переключить режим панелей"
              onClick={() => setLayoutMode((m) => (m === 'dual' ? 'single' : 'dual'))}
            >
              <ColumnWidthOutlined /> {layoutMode === 'dual' ? '1 панель' : '2 панели'}
            </button>
            {activeCount > 0 ? (
              <button
                type="button"
                className={styles.layoutToggle}
                onClick={() => setTransferOpen(true)}
              >
                Передачи ({activeCount})
              </button>
            ) : null}
            <button
              type="button"
              className={styles.iconBtn}
              title="Сравнить 2 файла"
              disabled={selected.size !== 2}
              onClick={() => void compareSelectedFiles()}
            >
              <DiffOutlined />
            </button>
            <button
              type="button"
              className={styles.iconBtn}
              title="Переключить вид"
              onClick={() => setViewMode((v) => (v === 'table' ? 'grid' : 'table'))}
            >
              {viewMode === 'table' ? <AppstoreOutlined /> : <UnorderedListOutlined />}
            </button>
            <button type="button" className={styles.iconBtn} title="Закрыть" onClick={handleClose}>
              <CloseOutlined />
            </button>
          </div>
        </header>

        <div className={styles.toolbar}>
          <div className={styles.toolbarGroups}>
            <div className={styles.toolbarGroup}>
              <span className={styles.toolbarLabel}>Навигация</span>
              <div className={styles.toolbarBtns}>
                <button
                  type="button"
                  className={styles.toolBtn}
                  title="Назад (Alt+←)"
                  disabled={!historyBack.length}
                  onClick={() => {
                    const prev = historyBack[historyBack.length - 1]
                    setHistoryBack((h) => h.slice(0, -1))
                    setHistoryForward((h) => [...h, currentPath])
                    if (connectionId) void loadDirectory(connectionId, prev, false)
                  }}
                >
                  <ArrowLeftOutlined />
                </button>
                <button
                  type="button"
                  className={styles.toolBtn}
                  title="Вперёд"
                  disabled={!historyForward.length}
                  onClick={() => {
                    const next = historyForward[historyForward.length - 1]
                    setHistoryForward((h) => h.slice(0, -1))
                    setHistoryBack((h) => [...h, currentPath])
                    if (connectionId) void loadDirectory(connectionId, next, false)
                  }}
                >
                  <ArrowRightOutlined />
                </button>
                <button
                  type="button"
                  className={styles.toolBtn}
                  title="Вверх"
                  disabled={currentPath === '/'}
                  onClick={() => navigateTo(parentPath(currentPath))}
                >
                  <ArrowUpOutlined />
                </button>
                <button
                  type="button"
                  className={styles.toolBtn}
                  title="Обновить (F5)"
                  disabled={!connectionId}
                  onClick={() => connectionId && void loadDirectory(connectionId, currentPath, false)}
                >
                  <ReloadOutlined />
                </button>
              </div>
            </div>
            <div className={styles.toolbarGroup}>
              <span className={styles.toolbarLabel}>Файлы</span>
              <div className={styles.toolbarBtns}>
                <button type="button" className={styles.toolBtn} title="Создать папку" disabled={!connectionId} onClick={() => void handleMkdir()}>
                  <FolderAddOutlined />
                </button>
                <button type="button" className={styles.toolBtn} title="Создать файл" disabled={!connectionId} onClick={() => void handleMkfile()}>
                  <FileAddOutlined />
                </button>
                <button
                  type="button"
                  className={styles.toolBtn}
                  title="Загрузить"
                  disabled={!connectionId}
                  onClick={() => uploadRef.current?.click()}
                >
                  <UploadOutlined />
                </button>
              </div>
            </div>
            <div className={styles.toolbarGroup}>
              <span className={styles.toolbarLabel}>Буфер</span>
              <div className={styles.toolbarBtns}>
                <button type="button" className={styles.toolBtn} title="Копировать" disabled={!hasSelection} onClick={handleCopy}>
                  <CopyOutlined />
                </button>
                <button type="button" className={styles.toolBtn} title="Вырезать" disabled={!hasSelection} onClick={handleCut}>
                  <ScissorOutlined />
                </button>
                <button type="button" className={styles.toolBtn} title="Вставить" disabled={!canPaste} onClick={() => void handlePaste()}>
                  <SnippetsOutlined />
                </button>
                <button type="button" className={styles.toolBtn} title="Удалить" disabled={!hasSelection} onClick={() => void handleDelete()}>
                  <DeleteOutlined />
                </button>
                <button type="button" className={styles.toolBtn} title="Переименовать (F2)" disabled={!singleSelection} onClick={() => void handleRename()}>
                  <EditOutlined />
                </button>
                <button
                  type="button"
                  className={styles.toolBtn}
                  title="Версии файла"
                  disabled={!singleSelection}
                  onClick={() => {
                    const path = Array.from(selected)[0]
                    const item = items.find((i) => i.path === path)
                    if (item) void showVersions(item)
                  }}
                >
                  <HistoryOutlined />
                </button>
              </div>
            </div>
          </div>
          <div className={styles.searchWrap}>
            <SearchOutlined className={styles.searchIcon} />
            <input
              className={styles.searchInput}
              placeholder="Поиск файлов..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className={styles.main}>
          {layoutMode === 'single' ? (
          <aside className={styles.sidebar}>
            <div className={styles.sidebarTabs}>
              {(['folders', 'favorites', 'recent'] as const).map((tab) => (
                <button
                  key={tab}
                  type="button"
                  className={`${styles.sidebarTab} ${sidebarTab === tab ? styles.sidebarTabActive : ''}`}
                  onClick={() => setSidebarTab(tab)}
                >
                  {tab === 'folders' ? 'Папки' : tab === 'favorites' ? 'Избранное' : 'Недавние'}
                </button>
              ))}
            </div>
            <div className={styles.sidebarContent}>
              {sidebarTab === 'folders' &&
                (folderTree.length ? (
                  folderTree.map((f) => (
                    <button
                      key={f.path}
                      type="button"
                      className={styles.sidebarItem}
                      onClick={() => navigateTo(f.path)}
                    >
                      <FolderOutlined />
                      <span>{f.name}</span>
                    </button>
                  ))
                ) : (
                  <div className={styles.emptyState}>Нет папок</div>
                ))}
              {sidebarTab === 'favorites' &&
                (favorites.length ? (
                  favorites.map((f) => (
                    <button
                      key={f.path}
                      type="button"
                      className={styles.sidebarItem}
                      onClick={() => navigateTo(f.path)}
                      onContextMenu={(e) => {
                        e.preventDefault()
                        void ftpRemoveFavorite(f.path).then(() => loadFavorites())
                      }}
                      title="ПКМ — удалить из избранного"
                    >
                      <StarOutlined />
                      <span>{f.path}</span>
                    </button>
                  ))
                ) : (
                  <div className={styles.emptyState}>Пусто</div>
                ))}
              {sidebarTab === 'recent' &&
                (recent.length ? (
                  recent.map((r) => (
                    <button
                      key={`${r.path}-${r.timestamp ?? r.name}`}
                      type="button"
                      className={styles.sidebarItem}
                      onClick={() => navigateTo(r.path)}
                    >
                      {r.type === 'directory' ? <FolderOutlined /> : <FileOutlined />}
                      <span>{r.name}</span>
                    </button>
                  ))
                ) : (
                  <div className={styles.emptyState}>Пусто</div>
                ))}
            </div>
          </aside>
          ) : null}

          {layoutMode === 'dual' ? (
            <FtpLocalPanel
              rootLabel={localRootLabel}
              entries={localEntries}
              currentPath={localPath}
              pathInput={localPathInput}
              selected={localSelected}
              searchQuery={searchQuery}
              sort={sort}
              onPathInputChange={setLocalPathInput}
              onNavigate={(p) => {
                const n = normalizePath(p)
                setLocalPath(n)
                setLocalPathInput(n)
                setLocalSelected(new Set())
              }}
              onSelect={toggleLocalSelect}
              onOpen={openLocalItem}
              onPickFolder={() => localDirRef.current?.click()}
              onAddFiles={() => localFileRef.current?.click()}
              onDropFiles={handleLocalDrop}
              onToggleSort={(field) =>
                setSort((prev) =>
                  prev.field === field
                    ? { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
                    : { field, direction: 'asc' },
                )
              }
            />
          ) : null}

          {layoutMode === 'dual' ? (
            <div className={styles.transferBar}>
              <button
                type="button"
                className={`${styles.transferBarBtn} ${styles.transferBarBtnPrimary}`}
                title="Загрузить на сервер"
                disabled={!connectionId || localSelected.size === 0}
                onClick={uploadLocalSelection}
              >
                <ArrowRightOutlined />
              </button>
              <button
                type="button"
                className={styles.transferBarBtn}
                title="Скачать в локальную панель"
                disabled={!connectionId || selected.size === 0}
                onClick={downloadRemoteSelection}
              >
                <ArrowLeftOutlined />
              </button>
            </div>
          ) : null}

          <main className={styles.fileArea}>
            {layoutMode === 'dual' ? (
              <div className={styles.panelHeader}>
                <span className={styles.panelTitle}>Удалённый сервер</span>
              </div>
            ) : null}
            <div className={styles.pathBar}>
              <button type="button" className={styles.toolBtn} title="Корень" onClick={() => navigateTo('/')}>
                <HomeOutlined />
              </button>
              <div className={styles.breadcrumb}>
                {crumbs.map((c, i) => (
                  <span key={c.path}>
                    {i > 0 ? ' / ' : null}
                    <button type="button" className={styles.crumb} onClick={() => navigateTo(c.path)}>
                      {c.label}
                    </button>
                  </span>
                ))}
              </div>
              <input
                className={styles.pathInput}
                value={pathInput}
                onChange={(e) => setPathInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') navigateTo(pathInput)
                }}
              />
            </div>

            <div
              className={styles.viewContainer}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault()
                void handleUpload(e.dataTransfer.files)
              }}
            >
              {listLoading ? (
                <div className={styles.loadingOverlay}>
                  <Spin />
                </div>
              ) : null}

              {connState === 'error' ? (
                <div className={styles.emptyState}>
                  Не удалось подключиться. Закройте окно и попробуйте снова.
                </div>
              ) : viewMode === 'table' ? (
                <table className={styles.fileTable}>
                  <thead>
                    <tr>
                      <th onClick={() => toggleSort('name')}>
                        Имя <span className={sort.field === 'name' ? styles.sortIconActive : styles.sortIcon}>{sortMark('name')}</span>
                      </th>
                      <th onClick={() => toggleSort('size')}>
                        Размер <span className={sort.field === 'size' ? styles.sortIconActive : styles.sortIcon}>{sortMark('size')}</span>
                      </th>
                      <th onClick={() => toggleSort('date')}>
                        Дата <span className={sort.field === 'date' ? styles.sortIconActive : styles.sortIcon}>{sortMark('date')}</span>
                      </th>
                      <th>Права</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map((item) => (
                      <tr
                        key={item.path}
                        className={selected.has(item.path) ? styles.rowSelected : undefined}
                        onClick={(e) => toggleSelect(item, e)}
                        onDoubleClick={() => void openItem(item)}
                        onContextMenu={(e) => {
                          e.preventDefault()
                          setContextItem(item)
                          setContextPos({ x: e.clientX, y: e.clientY })
                          if (!selected.has(item.path)) setSelected(new Set([item.path]))
                        }}
                      >
                        <td>
                          <div className={styles.nameCell}>
                            <span className={styles.fileIcon}>
                              {isDirectory(item) ? <FolderOutlined /> : <FileOutlined />}
                            </span>
                            {item.name}
                          </div>
                        </td>
                        <td>{isDirectory(item) ? '—' : formatFileSize(item.size)}</td>
                        <td>{formatDate(item.modifyTime)}</td>
                        <td>{formatPermissionsLabel(item.permissions)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className={styles.grid}>
                  {filteredItems.map((item) => (
                    <div
                      key={item.path}
                      className={`${styles.gridCard} ${selected.has(item.path) ? styles.gridCardSelected : ''}`}
                      onClick={(e) => toggleSelect(item, e)}
                      onDoubleClick={() => void openItem(item)}
                      onContextMenu={(e) => {
                        e.preventDefault()
                        setContextItem(item)
                        setContextPos({ x: e.clientX, y: e.clientY })
                      }}
                    >
                      <span className={styles.gridIcon}>
                        {isDirectory(item) ? <FolderOutlined /> : <FileOutlined />}
                      </span>
                      <span className={styles.gridName}>{item.name}</span>
                    </div>
                  ))}
                </div>
              )}

              {!listLoading && connState === 'connected' && !filteredItems.length ? (
                <div className={styles.emptyState}>Папка пуста</div>
              ) : null}
            </div>

            <div className={styles.statusBar}>
              <div className={styles.statusPills}>
                <span className={styles.pill}>{itemCount} элементов ({folderCount} папок, {fileCount} файлов)</span>
                <span className={styles.pill}>{selected.size} выбрано</span>
                <span className={styles.pill}>{formatFileSize(totalSize(filteredItems))}</span>
              </div>
              <span>{statusText}</span>
            </div>
          </main>
        </div>

        <FtpTransferPanel
          tasks={transferTasks}
          open={transferOpen}
          onToggle={() => setTransferOpen((v) => !v)}
          onClearDone={clearDone}
        />

        <div className={styles.logPanel}>
          <div className={styles.logHeader} onClick={() => setLogOpen((v) => !v)}>
            <span>Журнал ({logs.length})</span>
            <span>{logOpen ? '▼' : '▲'}</span>
          </div>
          {logOpen ? (
            <div className={styles.logContent}>
              {logs.map((l) => (
                <div
                  key={l.id}
                  className={`${styles.logLine} ${l.type === 'success' ? styles.logSuccess : l.type === 'error' ? styles.logError : ''}`}
                >
                  [{l.time}] {l.message}
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      <input
        ref={localDirRef}
        type="file"
        className={styles.hiddenInput}
        {...({ webkitdirectory: '', directory: '' } as Record<string, string>)}
        multiple
        onChange={(e) => {
          handleLocalFolderPick(e.target.files)
          e.target.value = ''
        }}
      />
      <input
        ref={localFileRef}
        type="file"
        multiple
        className={styles.hiddenInput}
        onChange={(e) => {
          if (e.target.files?.length) handleLocalDrop(e.target.files)
          e.target.value = ''
        }}
      />

      <FtpVersionsModal
        state={versionsState}
        loading={versionsLoading}
        onClose={() => setVersionsState(null)}
        onRestore={(index) => void restoreVersion(index)}
        onPreview={(content) => setVersionPreview(content)}
      />

      <FtpDiffModal
        state={compareState}
        loading={compareLoading}
        onClose={() => setCompareState(null)}
      />

      {versionPreview !== null ? (
        <div className={styles.subModal}>
          <div className={`${styles.subModalContent} ${styles.subModalWide}`}>
            <div className={styles.subModalHeader}>
              <h3>Просмотр версии</h3>
              <button
                type="button"
                className={styles.iconBtn}
                style={{ color: 'var(--text-primary)', background: 'var(--bg-hover)' }}
                onClick={() => setVersionPreview(null)}
              >
                <CloseOutlined />
              </button>
            </div>
            <div className={styles.subModalBody}>
              <pre className={styles.diffPre}>{versionPreview}</pre>
            </div>
          </div>
        </div>
      ) : null}

      <input
        ref={uploadRef}
        type="file"
        multiple
        className={styles.hiddenInput}
        onChange={(e) => {
          void handleUpload(e.target.files)
          e.target.value = ''
        }}
      />

      {contextPos && contextItem ? (
        <Dropdown
          open
          menu={{ items: contextMenuItems, onClick: ({ key }) => onContextAction(key) }}
          trigger={[]}
        >
          <div style={{ position: 'fixed', left: contextPos.x, top: contextPos.y, width: 1, height: 1 }} />
        </Dropdown>
      ) : null}

      {editor ? (
        <div className={styles.subModal}>
          <div className={`${styles.subModalContent} ${styles.subModalWide}`}>
            <div className={styles.subModalHeader}>
              <h3>{editor.name}</h3>
              <button type="button" className={styles.iconBtn} style={{ color: 'var(--text-primary)', background: 'var(--bg-hover)' }} onClick={() => setEditor(null)}>
                <CloseOutlined />
              </button>
            </div>
            <div className={styles.subModalBody}>
              <textarea
                ref={editorRef}
                className={styles.editor}
                value={editor.content}
                spellCheck={false}
                onChange={(e) => setEditor({ ...editor, content: e.target.value })}
                onSelect={() => {
                  const el = editorRef.current
                  if (!el) return
                  const text = el.value.substring(0, el.selectionStart)
                  const lines = text.split('\n')
                  const line = lines.length
                  const col = lines[lines.length - 1].length + 1
                  el.dataset.linecol = `${line}:${col}`
                }}
              />
            </div>
            <div className={styles.subModalFooter}>
              <span className={styles.editorInfo}>
                {editorRef.current?.dataset.linecol || '1:1'} | UTF-8
              </span>
              <div className={styles.footerActions}>
                <button type="button" className={styles.btnSecondary} onClick={() => setEditor(null)}>
                  Отмена
                </button>
                <button type="button" className={styles.btnPrimary} onClick={() => void saveEditor()}>
                  Сохранить (Ctrl+S)
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {viewer ? (
        <div className={styles.subModal}>
          <div className={`${styles.subModalContent} ${styles.subModalWide}`}>
            <div className={styles.subModalHeader}>
              <h3>{viewer.name}</h3>
              <button
                type="button"
                className={styles.iconBtn}
                style={{ color: 'var(--text-primary)', background: 'var(--bg-hover)' }}
                onClick={() => {
                  if (viewer.url.startsWith('blob:')) URL.revokeObjectURL(viewer.url)
                  setViewer(null)
                }}
              >
                <CloseOutlined />
              </button>
            </div>
            <div className={styles.viewerBody}>
              {viewer.kind === 'image' ? (
                <img src={viewer.url} alt={viewer.name} />
              ) : (
                <iframe src={viewer.url} title={viewer.name} />
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
